<template>
  <div class="px-2">
    <vuetable
      style="border: 4px solid rgba(0, 0, 0, 0.05);"
      ref="vuetable"
      :api-url="_api_url"
      :httpOptions="{ headers: { authorization: this._access_token } }"
      :fields="fields"
      data-path="results"
      :perPage="parseInt(pageSize)"
      :multiSort="true"
      pagination-path=""
      :queryParams="{ sort: 'ordering', page: 'page', perPage: 'page_size' }"
      @vuetable:pagination-data="onPaginationData"
      @vuetable:loading="handleLoading"
      @vuetable:loaded="handleLoaded"
      @vuetable:row-clicked="handleRowClicked"
      @vuetable:cell-clicked="handleCellClicked"
      @query="handleQuery"
      row-class="datagrid-row"
      :filterParams="filterParams"
      :ready="ready"
    >
      <div slot="_actions-slot" slot-scope="props">
        <d-dropdown
          theme="light"
          size="sm"
          noCaret
          class="mx-2"
          right
          closeOnClick
        >

          <template v-slot:button-content>
            <i class="material-icons">more_horiz</i>
          </template>
          <template v-for="action in actions">
            <d-dropdown-item
              v-if="!!action.condition ? action.condition(props.rowData) !== false : true"
              :key="action.name"
              @click="handleRowAction(action, props)"
              tag="button"
              :icon="action.icon"
              :iconTheme="action.theme"
              class="btn-sm"
            >
              {{ action.name }}
            </d-dropdown-item>
          </template>
        </d-dropdown>
      </div>
      <div
        slot="loading"
        class="d-flex justify-content-center align-items-center"
      >
        <odd-spinner :size="36" class="my-5" />
      </div>
    </vuetable>
    <d-row class="p-2">
      <d-col
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-start"
      >
        <DropDownCheckList
          :items="pageSizeOptions"
          :text="`Rows per page: ${pageSize}`"
          v-model="pageSize"
          theme="clear"
          :noCaret="false"
          radio
        />
      </d-col>
      <d-col
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end"
      >
        <keep-alive>
          <TablePaginationInfo :class="['mx-2 small']" ref="paginationInfo" />
        </keep-alive>

        <keep-alive>
        <TablePagination
          :class="[]"
          ref="pagination"
          :showFirstLastButtons="true"
          @vuetable-pagination:change-page="onChangePage"
        />
        </keep-alive>
      </d-col>
    </d-row>
  </div>
</template>

<script>
import Vuetable from "@/components/DataGrid/Vuetable/Vuetable.vue";
import TablePagination from "@/components/DataGrid/Vuetable/VuetablePagination.vue";
import TablePaginationInfo from "@/components/DataGrid/Vuetable/VuetablePaginationInfo.vue";
import DropDownCheckList from "@/components/DropDownChecklist.vue";

export default {
  name: "odd-table",
  props: {
    viewsetPath: {
      type: String,
      required: true
    },
    fields: {
      type: Array,
      required: true
    },
    actions: {
      type: Array,
      required: false
    },
    filters: {
      type: Object,
      required: false
    },
    search: {
      type: String,
      required: false
    },
    paginationCssClass: {
      type: String
    },
    refresh: {
      type: Number,
      default: 0
    },
    ready: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      paginationData: {
        total: null,
        per_page: null,
        current_page: null,
        last_page: null,
        next_page_url: null,
        prev_page_url: null,
        from: 0,
        to: 25
      },
      loading: true,
      selected: true,
      completed: false,
      pageSize: "10",
      pageSizeOptions: [
        { name: "10", title: "10" },
        { name: "25", title: "25" },
        { name: "50", title: "50" }
      ]
    };
  },
  components: {
    Vuetable,
    TablePagination,
    TablePaginationInfo,
    DropDownCheckList
  },
  watch: {
    fields: {
      deep: true,
      handler() {
        return;
      }
    },
    filters: {
      deep: true,
      handler() {
        return;
      }
    },
    refresh() {
      this.$refs.vuetable.reload();
    }
  },
  created() {
    this.auth_key = `Bearer ${localStorage.getItem("token")}`;
  },
  methods: {
    toggleDetailRow() {
      this.$refs.vuetable.toggleDetailRow(paginationData.results[2].id);
    },
    onPaginationData(paginationData) {
      this.paginationData.total = paginationData.count;
      this.paginationData.per_page = paginationData.page_size;
      this.paginationData.current_page = paginationData.page_num;
      this.paginationData.last_page = paginationData.last_page;
      this.paginationData.next_page_url = paginationData.next;
      this.paginationData.prev_page_url = paginationData.prev;
      this.paginationData.from =
        paginationData.page_size * (paginationData.page_num - 1) + 1;
      this.paginationData.to = Math.min(
        paginationData.page_size * paginationData.page_num,
        paginationData.count
      );
      this.$refs.pagination.setPaginationData(this.paginationData);
      this.$refs.paginationInfo.setPaginationData(this.paginationData);
    },
    // when the user click something that causes the page to change,
    // call "changePage" method in Vuetable, so that that page will be
    // requested from the API endpoint.
    onChangePage(page) {
      this.$refs.vuetable.changePage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    handleLoading() {
      this.loading = true;
      this.$emit("loading", this.loading);
    },
    handleLoaded() {
      this.loading = false;
      this.$emit("loading", this.loading);
    },
    handleRowClicked(e) {},
    handleCellClicked(e) {},
    handleRowAction(action, props) {
      this.$emit("rowAction", { action, props });
    },
    handleQuery(query) {
      this.$emit('query', query)
    }
  },
  computed: {
    _api_url() {
      const base =
        process.env.NODE_ENV === "development"
          ? "http://localhost:8000"
          : "https://api.oddular.com";
      return base + this.viewsetPath;
    },
    _access_token() {
      return `Bearer ${this.$store.state.auth.access}`;
    },
    filterParams() {
      return { search: this.search, ...this.filters };
    },
    httpOptions() {
      return {
        config: {
          headers: {
            authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      };
    }
  }
};
</script>

<style lang="scss">
@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.datagrid-row {
  animation-name: fadeIn;
  animation-duration: 1s;

  // &:hover {
  //   background: rgba(0,0,0, .02);
  //   cursor: pointer;
  // }
}
</style>
